/**
 * SalesOnboarding — Onboarding-Übersicht + Detail-Modal.
 *
 * @page /sales/onboarding
 * @roles admin, manager
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import {
  X, CheckCircle2, Clock, AlertCircle, Plus, ChevronDown, ExternalLink,
  Trash2, Calendar, CircleDot, XCircle, Circle, Send, MessageSquare,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts, credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

const DEPT_COLORS = {
  customer_success: 'bg-primary-50 text-primary-700 border-primary-200',
  it: 'bg-blue-50 text-blue-700 border-blue-200',
  buchhaltung: 'bg-amber-50 text-amber-700 border-amber-200',
};

const STATUS_CONFIG = {
  offen:          { icon: Circle,       color: '#9CA3AF' },
  in_bearbeitung: { icon: CircleDot,    color: '#6366F1' },
  erledigt:       { icon: CheckCircle2, color: '#16A34A' },
  blockiert:      { icon: XCircle,      color: '#EF4444' },
};

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct === 100 ? '#16A34A' : pct >= 67 ? '#338edc' : pct >= 34 ? '#F59E0B' : '#E5E7EB';
  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      <div className="flex-1 h-2 min-w-[60px] bg-[var(--bg-inset)] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[11px] text-[var(--text-secondary)] whitespace-nowrap">{done}/{total}</span>
    </div>
  );
}

// ── Fortschrittsring (SVG Donut) ─────────────────────────────

function FortschrittsRing({ erledigt, gesamt, size = 72 }) {
  const percent = gesamt > 0 ? Math.round((erledigt / gesamt) * 100) : 0;
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  const farbe =
    percent === 100 ? '#16A34A' :
    percent >= 67   ? '#338edc' :
    percent >= 34   ? '#F59E0B' :
                      '#E5E7EB';
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="var(--bg-inset)" strokeWidth={7} />
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={farbe} strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute text-center">
        <span className="text-[13px] font-bold text-[var(--text-primary)]">{percent}%</span>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, iconColor }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className={iconColor || 'text-[#338edc]'} />}
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{label}</p>
      </div>
      <p className="text-3xl font-bold text-[var(--text-primary)]">{value}</p>
    </div>
  );
}

// ── Aufgaben-Zeile (expandierbar) ────────────────────────────

function AufgabenZeile({ aufgabe, users, onStatusChange, onAssign, onReload, t }) {
  const [expanded, setExpanded] = useState(false);
  const [kommentare, setKommentare] = useState([]);
  const [kommentarText, setKommentarText] = useState('');
  const [sending, setSending] = useState(false);

  const isOverdue = aufgabe.status !== 'erledigt' && aufgabe.faellig_am && new Date(aufgabe.faellig_am) < new Date(new Date().toDateString());
  const cfg = STATUS_CONFIG[aufgabe.status] || STATUS_CONFIG.offen;
  const StatusIcon = cfg.icon;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }) : '';

  const loadKommentare = async () => {
    try {
      const data = await api(`/api/onboarding/aufgaben/${aufgabe.id}/kommentare`);
      setKommentare(data);
    } catch { /* */ }
  };

  const toggleExpand = () => {
    if (!expanded) loadKommentare();
    setExpanded(!expanded);
    setKommentarText('');
  };

  const sendKommentar = async () => {
    if (!kommentarText.trim() || sending) return;
    setSending(true);
    try {
      await api(`/api/onboarding/aufgaben/${aufgabe.id}/kommentare`, {
        method: 'POST', body: { text: kommentarText.trim() },
      });
      setKommentarText('');
      loadKommentare();
    } catch { /* */ }
    setSending(false);
  };

  return (
    <div className="border-b border-[var(--border-card)] last:border-0">
      {/* Main Row */}
      <div
        onClick={toggleExpand}
        className="flex items-center gap-3 py-3 cursor-pointer hover:bg-[var(--bg-inset)] rounded-lg px-2 -mx-2 transition-colors"
      >
        <StatusIcon size={18} style={{ color: cfg.color, flexShrink: 0 }} />
        <span className={`text-[13px] flex-1 ${aufgabe.status === 'erledigt' ? 'line-through text-[var(--text-tertiary)]' : 'text-[var(--text-primary)]'}`}>
          {aufgabe.titel}
        </span>
        {aufgabe.zugewiesen_name && (
          <span className="text-[11px] text-[var(--text-secondary)] bg-[var(--bg-inset)] px-2 py-0.5 rounded-full">
            {aufgabe.zugewiesen_name}
          </span>
        )}
        {aufgabe.faellig_am && (
          <span className={`text-[11px] flex-shrink-0 ${isOverdue ? 'text-[#EF4444] font-medium' : 'text-[var(--text-tertiary)]'}`}>
            {formatDate(aufgabe.faellig_am)}
          </span>
        )}
        <ChevronDown size={14} className={`text-[var(--text-tertiary)] transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {/* Expanded Panel */}
      {expanded && (
        <div className="mx-2 mb-3 p-4 bg-[var(--bg-inset)] rounded-xl border border-[var(--border-card)]">
          {/* Status Buttons */}
          <div className="mb-3">
            <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2 uppercase tracking-wide">
              {t('sales.onboarding.statusAendern')}
            </p>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                const Icon = c.icon;
                const active = aufgabe.status === key;
                return (
                  <button
                    key={key}
                    onClick={() => onStatusChange(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${
                      active
                        ? 'bg-[var(--bg-card)] border-[#338edc] text-[#338edc]'
                        : 'bg-[var(--bg-card)] border-[var(--border-card)] text-[var(--text-secondary)] hover:border-[#338edc]'
                    }`}
                  >
                    <Icon size={12} style={{ color: active ? '#338edc' : c.color }} />
                    {t(`sales.onboarding.taskStatus.${key}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Assignment */}
          <div className="mb-3">
            <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2 uppercase tracking-wide">
              {t('sales.onboarding.zugewiesenAn')}
            </p>
            <select
              value={aufgabe.zugewiesen_an || ''}
              onChange={(e) => onAssign(e.target.value ? parseInt(e.target.value) : null)}
              className="text-[12px] border border-[var(--border-input)] rounded-lg px-3 py-1.5 bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
            >
              <option value="">— {t('sales.onboarding.keinStandard')} —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          {/* Erledigt Info */}
          {aufgabe.erledigt_am && (
            <p className="text-[10px] text-[var(--text-tertiary)] mb-3">
              {t('sales.onboarding.taskStatus.erledigt')}: {new Date(aufgabe.erledigt_am).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              {aufgabe.erledigt_von_name && ` von ${aufgabe.erledigt_von_name}`}
            </p>
          )}

          {aufgabe.beschreibung && (
            <p className="text-[12px] text-[var(--text-secondary)] mb-3">{aufgabe.beschreibung}</p>
          )}

          {/* Comments */}
          <div>
            <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2 uppercase tracking-wide flex items-center gap-1">
              <MessageSquare size={12} />
              {t('sales.onboarding.kommentare')} ({kommentare.length})
            </p>
            {kommentare.length > 0 && (
              <div className="space-y-2 mb-2 max-h-32 overflow-y-auto">
                {kommentare.map(k => (
                  <div key={k.id} className="flex gap-2 text-[12px]">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0" style={{ background: '#338edc' }}>
                      {(k.user_name || '?')[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[11px] text-[var(--text-primary)]">{k.user_name}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)]">{formatDate(k.created_at)}</span>
                      </div>
                      <p className="text-[11px] text-[var(--text-secondary)]">{k.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={kommentarText}
                onChange={e => setKommentarText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendKommentar()}
                placeholder={t('sales.onboarding.kommentarPlaceholder')}
                className="flex-1 text-[11px] px-3 py-1.5 border border-[var(--border-input)] rounded-lg bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              />
              <button
                onClick={sendKommentar}
                disabled={sending || !kommentarText.trim()}
                className="p-1.5 rounded-lg transition-colors"
                style={{ background: kommentarText.trim() ? '#338edc' : 'var(--bg-card)', color: kommentarText.trim() ? '#fff' : '#9CA3AF' }}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────

function OnboardingCommitments({ onboardingId }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/onboarding/${onboardingId}/commitments`)
      .then((d) => setItems(d.commitments || d.items || d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [onboardingId]);

  if (loading) return null;

  return (
    <div className="mt-4 bg-[var(--bg-inset)] rounded-[12px] p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
          {t('commitment.section_onboarding') || 'Vereinbarungen'}
        </h4>
        <a href={`/commitments/new?onboarding_id=${onboardingId}`} className="text-[11px] text-[#338edc] hover:underline">+ {t('commitment.new') || 'Neue Vereinbarung'}</a>
      </div>
      {items.length === 0 ? (
        <div className="text-[12px] text-[var(--text-tertiary)] italic">
          {t('commitment.empty_onboarding') || 'Noch keine Vereinbarungen erfasst.'}
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((c) => (
            <a
              key={c.id}
              href={`/commitments/${c.id}`}
              className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-white/50 text-[12px]"
            >
              <span className="truncate text-[var(--text-primary)]">
                <span className="font-mono text-[10px] text-[var(--text-tertiary)] mr-2">{c.commitment_nr}</span>
                {c.titel}
              </span>
              <span className="text-[10px] text-[var(--text-secondary)] uppercase">{c.status}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function OnboardingDetail({ id, mitarbeiter, onClose, onUpdate }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ titel: '', abteilung: 'customer_success', faellig_am: '' });

  const load = async () => {
    try {
      const d = await api(`/api/onboarding/${id}`);
      setData(d);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const updateTaskStatus = async (taskId, newStatus) => {
    if (newStatus === 'erledigt') {
      await api(`/api/onboarding/aufgaben/${taskId}/erledigen`, { method: 'POST' });
    } else {
      await api(`/api/onboarding/aufgaben/${taskId}`, { method: 'PUT', body: { status: newStatus } });
    }
    await load();
    onUpdate?.();
  };

  const assignTask = async (taskId, maId) => {
    try {
      await api(`/api/onboarding/aufgaben/${taskId}`, { method: 'PUT', body: { zugewiesen_an: maId || null } });
      await load();
    } catch (err) { console.error('assignTask error:', err); }
  };

  const handleGoLiveChange = async (value) => {
    try {
      await api(`/api/onboarding/${id}`, { method: 'PUT', body: { go_live_datum: value || null } });
      await load();
      onUpdate?.();
    } catch (err) { console.error(err); }
  };

  const handleOdooPush = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const res = await api(`/api/leads/${data.lead_id}/odoo-push`, { method: 'POST' });
      setPushResult(res);
      if (res.success) await load();
    } catch (err) {
      setPushResult({ success: false, error: err.message });
    } finally {
      setPushing(false);
    }
  };

  const completeOnboarding = async () => {
    setShowCompleteModal(false);
    await api(`/api/onboarding/${id}`, { method: 'PUT', body: { status: 'abgeschlossen' } });
    await load();
    onUpdate?.();
  };

  const addOptionalTask = async () => {
    if (!newTask.titel.trim()) return;
    await api(`/api/onboarding/${id}/aufgabe`, { method: 'POST', body: newTask });
    setNewTask({ titel: '', abteilung: 'customer_success', faellig_am: '' });
    setShowAddTask(false);
    await load();
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-card)] rounded-[16px] p-12">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full mx-auto" />
      </div>
    </div>
  );
  if (!data) return null;

  const aufgaben = data.aufgaben || [];
  const deptGroups = {};
  for (const a of aufgaben) {
    const dept = a.abteilung || 'sonstige';
    if (!deptGroups[dept]) deptGroups[dept] = [];
    deptGroups[dept].push(a);
  }
  const erledigtCount = aufgaben.filter(a => a.status === 'erledigt').length;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-[16px] border border-[var(--border-card)] shadow-dropdown w-full max-w-[960px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header with FortschrittsRing */}
        <div className="flex items-start gap-4 px-6 py-5 border-b border-[var(--border-card)]">
          <FortschrittsRing erledigt={erledigtCount} gesamt={aufgaben.length} size={72} />
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] font-semibold text-[var(--text-primary)]">
              {data.firmenname} — {t('sales.onboarding.title')}
            </h2>
            <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
              {erledigtCount}/{aufgaben.length} {t('sales.onboarding.aufgabenErledigt')}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Calendar size={14} className="text-[var(--text-tertiary)]" />
              <span className="text-[11px] text-[var(--text-secondary)]">{t('sales.onboarding.goLive')}:</span>
              <input
                type="date"
                value={data.go_live_datum ? data.go_live_datum.substring(0, 10) : ''}
                onChange={e => handleGoLiveChange(e.target.value)}
                className="text-[11px] border border-[var(--border-input)] rounded-lg px-2 py-1 bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              />
              {data.odoo_push_status === 'erledigt' && (
                <span className="text-[11px] text-emerald-600 flex items-center gap-1 ml-2">
                  <CheckCircle2 className="w-3 h-3" /> Odoo ID: {data.odoo_partner_id}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {data.lead_id && data.odoo_push_status !== 'erledigt' && (
              <button
                onClick={handleOdooPush}
                disabled={pushing}
                className="px-3 py-2 rounded-lg text-[12px] font-medium border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-40 flex items-center gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {pushing ? '...' : t('sales.onboarding.odooPush')}
              </button>
            )}
            {data.status === 'aktiv' && (
              <button
                onClick={() => setShowCompleteModal(true)}
                className="px-3 py-2 rounded-lg text-white text-[12px] font-semibold flex items-center gap-1.5 hover:opacity-90"
                style={{ background: '#338edc' }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t('sales.onboarding.completeOnboarding')}
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset)]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Push Result */}
        {pushResult && (
          <div className={`mx-6 mt-3 px-3 py-2 rounded-[8px] text-[12px] ${
            pushResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
          }`}>
            {pushResult.success
              ? `${t('sales.onboarding.odooPushSuccess')} (ID: ${pushResult.partner_id})`
              : `${t('sales.onboarding.odooPushError')}: ${pushResult.error}${pushResult.fehlende_felder ? ` — ${pushResult.fehlende_felder.join(', ')}` : ''}`
            }
          </div>
        )}

        {/* Tasks by department */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(deptGroups).map(([dept, tasks]) => (
              <div key={dept} className="bg-[var(--bg-inset)] rounded-[12px] p-4">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3">
                  {t(`sales.onboarding.departments.${dept}`) || dept}
                </h4>
                <div>
                  {tasks.map((task) => (
                    <AufgabenZeile
                      key={task.id}
                      aufgabe={task}
                      users={mitarbeiter}
                      onStatusChange={(s) => updateTaskStatus(task.id, s)}
                      onAssign={(maId) => assignTask(task.id, maId)}
                      onReload={load}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Commitments / Vereinbarungen (#930) */}
          <OnboardingCommitments onboardingId={id} />

          {/* Add optional task */}
          <div className="mt-4">
            {!showAddTask ? (
              <button onClick={() => setShowAddTask(true)} className="flex items-center gap-1.5 text-[12px] text-[var(--primary)] hover:underline">
                <Plus className="w-3.5 h-3.5" /> {t('sales.onboarding.addOptionalTask')}
              </button>
            ) : (
              <div className="bg-[var(--bg-inset)] rounded-[12px] p-4">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <input
                    value={newTask.titel}
                    onChange={(e) => setNewTask({ ...newTask, titel: e.target.value })}
                    placeholder="Titel..."
                    className="col-span-2 px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px]"
                  />
                  <select
                    value={newTask.abteilung}
                    onChange={(e) => setNewTask({ ...newTask, abteilung: e.target.value })}
                    className="px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px]"
                  >
                    <option value="customer_success">Customer Success</option>
                    <option value="it">IT</option>
                    <option value="buchhaltung">Buchhaltung</option>
                  </select>
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={newTask.faellig_am}
                    onChange={(e) => setNewTask({ ...newTask, faellig_am: e.target.value })}
                    className="px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px]"
                  />
                  <div className="flex gap-2 ml-auto">
                    <button onClick={() => setShowAddTask(false)} className="text-[12px] text-[var(--text-secondary)]">{t('common.cancel') || 'Abbrechen'}</button>
                    <button
                      onClick={addOptionalTask}
                      disabled={!newTask.titel.trim()}
                      className="px-3 py-1.5 rounded-[8px] text-white text-[12px] font-medium disabled:opacity-40"
                      style={{ background: 'var(--primary)' }}
                    >
                      {t('common.add') || 'Hinzufügen'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Complete Onboarding Confirmation Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setShowCompleteModal(false)}>
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-[var(--border-card)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('sales.onboarding.completeConfirm') || 'Onboarding abschließen?'}</h3>
                <p className="text-[13px] text-[var(--text-secondary)]">{data?.firmenname}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCompleteModal(false)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] border border-[var(--border-card)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={completeOnboarding} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-white bg-emerald-600 hover:bg-emerald-700">
                {t('sales.onboarding.completeOnboarding') || 'Abschließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function SalesOnboarding() {
  const { t } = useLanguage();
  const [eintraege, setEintraege] = useState([]);
  const [stats, setStats] = useState({});
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'alle') params.set('status', statusFilter);
      if (search) params.set('search', search);
      const [list, st, ma] = await Promise.all([
        api(`/api/onboarding?${params}`),
        api('/api/onboarding/stats'),
        api('/api/onboarding/users'),
      ]);
      setEintraege(list);
      setStats(st);
      setMitarbeiter(ma);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, search]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }) : '-';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">{t('sales.onboarding.title')}</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KpiCard icon={CircleDot} iconColor="text-[#338edc]" label={t('sales.onboarding.taskStatus.offen') || 'Aktiv'} value={stats.aktiv || 0} />
        <KpiCard icon={CheckCircle2} iconColor="text-[#10B981]" label={t('sales.onboarding.taskStatus.erledigt') || 'Abgeschlossen'} value={stats.abgeschlossen || 0} />
        <KpiCard icon={AlertCircle} iconColor="text-[#EF4444]" label={t('sales.onboarding.overdue') || 'Überfällig'} value={stats.ueberfaellig || 0} />
        <KpiCard icon={Calendar} iconColor="text-[#22D3EE]" label={t('sales.onboarding.goLiveThisWeek')} value={stats.go_live_diese_woche || 0} />
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px]"
        >
          <option value="alle">{t('common.all') || 'Alle'}</option>
          <option value="aktiv">{t('sales.onboarding.taskStatus.offen') || 'Aktiv'}</option>
          <option value="abgeschlossen">{t('sales.onboarding.taskStatus.erledigt') || 'Abgeschlossen'}</option>
          <option value="pausiert">Pausiert</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`${t('common.search') || 'Suchen'}...`}
          className="flex-1 max-w-xs px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px]"
        />
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('common.company') || 'Firma'}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">Start</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('sales.onboarding.goLive')}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] w-40">{t('sales.onboarding.progress')}</th>
              <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('sales.onboarding.overdue')}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">Status</th>
            </tr>
          </thead>
          <tbody>
            {eintraege.map((e) => {
              const ueb = parseInt(e.aufgaben_ueberfaellig) || 0;
              return (
                <tr
                  key={e.id}
                  onClick={() => setSelectedId(e.id)}
                  className="border-b border-[var(--border-table,#F3F4F6)] last:border-0 cursor-pointer hover:bg-[var(--bg-card-hover)] transition-colors"
                >
                  <td className="px-4 py-3 text-[13px] font-medium text-[var(--text-primary)]">{e.firmenname}</td>
                  <td className="px-4 py-3 text-[12px] text-[var(--text-secondary)]">{formatDate(e.created_at)}</td>
                  <td className="px-4 py-3 text-[12px] text-[var(--text-secondary)]">{formatDate(e.go_live_datum)}</td>
                  <td className="px-4 py-3">
                    <ProgressBar done={parseInt(e.aufgaben_erledigt) || 0} total={parseInt(e.aufgaben_gesamt) || 0} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {ueb > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-600 border border-red-200">
                        {ueb}
                      </span>
                    ) : (
                      <span className="text-[11px] text-[var(--text-tertiary)]">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      e.status === 'aktiv' ? 'bg-blue-50 text-blue-600 border-blue-200'
                      : e.status === 'abgeschlossen' ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                      : 'bg-gray-50 text-[var(--text-secondary,#4B5563)] border-[var(--border-default,#E5E7EB)]'
                    }`}>
                      {e.status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loading && eintraege.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[13px] text-[var(--text-tertiary)]">
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedId && (
        <OnboardingDetail
          id={selectedId}
          mitarbeiter={mitarbeiter}
          onClose={() => setSelectedId(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}
